import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Coins, Loader2, MessageCircle, ShieldCheck, ShoppingBag, Sparkles } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { TOKEN_PACKS } from "@/data/tokenPacks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { buildWhatsAppLink } from "@/lib/contact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAY_METHODS = [
  { id: "gcash", name: "GCash", color: "#00b9f1" },
  { id: "maya", name: "Maya", color: "#34d399" },
  { id: "grabpay", name: "GrabPay", color: "#22d3ee" },
  { id: "card", name: "Visa / Mastercard", color: "#a78bfa" },
  { id: "wa", name: "WhatsApp Pay", color: "#00cfff" },
];

export default function TokenBank() {
  const { t, lang, formatPrice } = useI18n();
  const [activePack, setActivePack] = useState(null);

  return (
    <section data-testid="token-bank" id="token-bank" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Coins className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />
            <span className="font-mono-d text-[11px] uppercase tracking-[0.32em] text-white/65">
              {t("token_bank_title")}
            </span>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            <span className="dncm-gradient-text">Token Bank</span>
          </h3>
          <p className="text-white/65 mt-2 max-w-xl">{t("token_bank_subtitle")}</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {PAY_METHODS.map((p) => (
            <span
              key={p.id}
              data-testid={`tokenbank-pay-${p.id}`}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono-d text-[10px] uppercase tracking-[0.22em]"
              style={{
                background: `${p.color}14`,
                border: `1px solid ${p.color}55`,
                color: "white",
                boxShadow: `0 0 14px ${p.color}22`,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
              {p.name}
            </span>
          ))}
        </div>
      </header>

      <div className="grid md:grid-cols-3 gap-5">
        {TOKEN_PACKS.map((pack, idx) => {
          const isMid = idx === 1;
          return (
            <article
              key={pack.id}
              data-testid={`tokenpack-${pack.id}`}
              className={`relative dncm-hud-frame ${pack.id === "growth" ? "green" : ""} rounded-2xl p-6 bg-black/60 dncm-fadein`}
              style={{ animationDelay: `${idx * 0.06}s` }}
            >
              {pack.badge && (
                <div
                  className="absolute -top-3 left-6 px-3 py-1 rounded-full font-mono-d text-[10px] uppercase tracking-[0.22em]"
                  style={{
                    background: pack.accent,
                    color: "#0b1400",
                    boxShadow: `0 0 18px ${pack.accent}88`,
                  }}
                >
                  {pack.badge[lang] || pack.badge.en}
                </div>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">
                    {t(pack.name_key)}
                  </div>
                  <div className="font-display text-2xl text-white mt-1">{pack.label}</div>
                </div>
                <span
                  className="flex items-center justify-center w-10 h-10 rounded-lg"
                  style={{
                    background: `${pack.accent}1A`,
                    border: `1px solid ${pack.accent}55`,
                    color: pack.accent,
                  }}
                >
                  <Coins className="w-5 h-5" />
                </span>
              </div>

              <div className="mt-5">
                <div className="font-display text-3xl text-white">
                  {formatPrice(pack.price_php)}
                </div>
                <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45 mt-1">
                  ≈ {(pack.price_php / pack.tokens * 1_000_000).toFixed(2)} PHP / 1M tokens
                </div>
              </div>

              <ul className="mt-5 space-y-1.5">
                {pack.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/70">
                    <span
                      className="mt-1.5 w-1.5 h-1.5 rounded-full"
                      style={{ background: pack.accent, boxShadow: `0 0 8px ${pack.accent}` }}
                    />
                    {b[lang] || b.en}
                  </li>
                ))}
              </ul>

              <button
                data-testid={`buy-pack-${pack.id}`}
                onClick={() => setActivePack(pack)}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] ${
                  isMid ? "dncm-offer-btn" : "text-[#001018] dncm-btn-glow"
                }`}
                style={
                  !isMid
                    ? { background: "linear-gradient(180deg, #6ff3ff 0%, #00cfff 60%, #00a6cf 100%)" }
                    : undefined
                }
              >
                <ShoppingBag className="w-4 h-4" />
                {t("buy_pack")}
              </button>
            </article>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-center gap-2 font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
        <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--dncm-green)]" />
        Paiement sécurisé · Activation immédiate
      </div>

      <BuyPackDialog pack={activePack} open={!!activePack} onOpenChange={(o) => !o && setActivePack(null)} />
    </section>
  );
}

function BuyPackDialog({ pack, open, onOpenChange }) {
  const { t, formatPrice, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!pack) return;
    if (!form.name.trim()) return toast.error(lang === "fr" ? "Merci d'indiquer votre nom." : "Please share your name.");
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim() || null,
        message: `Token pack ${pack.label} (${pack.price_php} PHP)`,
        source: "tokenbank",
        agent: `Pack ${pack.id}`,
      });
      toast.success(lang === "fr" ? "Commande reçue. Activation immédiate." : "Order received. Activation in progress.");
      setForm({ name: "", email: "" });
      onOpenChange(false);
    } catch {
      toast.error(lang === "fr" ? "Envoi impossible. Réessayez ou utilisez WhatsApp." : "Could not send. Try again or use WhatsApp.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#070a0c] border border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">
            {pack ? `${t(pack.name_key)} · ${pack.label}` : ""}
          </DialogTitle>
          <DialogDescription className="text-white/65">
            {pack ? `${formatPrice(pack.price_php)}` : ""}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="space-y-1.5">
            <Label htmlFor="bp-name" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("name")}</Label>
            <Input id="bp-name" data-testid="buy-pack-name" value={form.name} onChange={update("name")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-green)]" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bp-email" className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/55">{t("email")}</Label>
            <Input id="bp-email" type="email" data-testid="buy-pack-email" value={form.email} onChange={update("email")}
              className="bg-black/40 border-white/15 text-white focus-visible:ring-[color:var(--dncm-green)]" />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button type="submit" data-testid="buy-pack-submit" disabled={busy}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] dncm-offer-btn disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {t("confirm_order")}
            </button>
            <a
              data-testid="buy-pack-whatsapp"
              href={buildWhatsAppLink(pack ? `Bonjour, je veux acheter le pack ${pack.label}.` : "")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-mono-d text-[11px] uppercase tracking-[0.22em] text-white border"
              style={{ borderColor: "rgba(0,207,255,0.5)", background: "rgba(0,207,255,0.04)" }}
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
